import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { incidentsApi } from '../lib/api';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    AlertTriangle,
    RefreshCw,
    Plus,
    CheckCircle2,
    Clock,
    AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime, capitalize, cn } from '../lib/utils';

const categories = [
    { id: 'food_safety', label: 'Food Safety' },
    { id: 'equipment', label: 'Equipment' },
    { id: 'staff', label: 'Staff' },
    { id: 'customer', label: 'Customer' },
    { id: 'other', label: 'Other' }
];

const severities = [
    { id: 'low', label: 'Low', color: 'bg-sky-500/10 text-sky-400 border-sky-500/20' },
    { id: 'medium', label: 'Medium', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
    { id: 'high', label: 'High', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
    { id: 'critical', label: 'Critical', color: 'bg-rose-500/10 text-rose-400 border-rose-500/20' }
];

const Incidents = () => {
    const { isManagerOrAdmin: isManager } = useAuth();
    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
    const [selectedIncident, setSelectedIncident] = useState(null);
    
    // Form state
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('');
    const [severity, setSeverity] = useState('');
    const [correctiveAction, setCorrectiveAction] = useState('');

    useEffect(() => {
        loadIncidents();
    }, [filter]);

    const loadIncidents = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter === 'open') params.is_resolved = false;
            if (filter === 'resolved') params.is_resolved = true;
            
            const response = await incidentsApi.getAll(params);
            setIncidents(response.data);
        } catch (error) {
            console.error('Failed to load incidents:', error);
            toast.error('Failed to load incidents');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setCategory('');
        setSeverity('');
        setCorrectiveAction('');
    };

    const handleCreateIncident = async () => {
        if (!title || !description || !category || !severity) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            const response = await incidentsApi.create({
                title,
                description,
                category,
                severity
            });
            
            setIncidents([response.data, ...incidents]);
            setCreateDialogOpen(false);
            resetForm();
            toast.success('Incident reported successfully');
        } catch (error) {
            console.error('Failed to report incident:', error);
            toast.error('Failed to report incident');
        }
    };

    const handleResolveIncident = async () => {
        if (!correctiveAction) {
            toast.error('Please enter the corrective action taken');
            return;
        }

        try {
            const response = await incidentsApi.resolve(selectedIncident.id, correctiveAction);
            setIncidents(incidents.map(i => i.id === selectedIncident.id ? response.data : i));
            setResolveDialogOpen(false);
            setSelectedIncident(null);
            setCorrectiveAction('');
            toast.success('Incident resolved successfully');
        } catch (error) {
            console.error('Failed to resolve incident:', error);
            toast.error('Failed to resolve incident');
        }
    };

    const getSeverityBadge = (sev) => {
        const found = severities.find(s => s.id === sev);
        return found || severities[0];
    };

    if (loading && !incidents.length) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-8 w-8 brand-icon animate-spin" />
                </div>
            </Layout>
        );
    }

    const openCount = incidents.filter(i => !i.is_resolved).length;

    return (
        <Layout>
            <div className="space-y-6" data-testid="incidents-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-100">Incidents</h1>
                        <p className="text-slate-400 mt-1">
                            Report and track food safety incidents
                        </p>
                    </div>
                    <Dialog open={createDialogOpen} onOpenChange={(open) => {
                        setCreateDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        <DialogTrigger asChild>
                            <Button className="brand-btn" data-testid="report-incident-button">
                                <Plus className="h-4 w-4 mr-2" />
                                Report Incident
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-800">
                            <DialogHeader>
                                <DialogTitle className="text-slate-100">Report Incident</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 pt-4">
                                <div className="space-y-2">
                                    <Label className="text-slate-300">Title *</Label>
                                    <Input
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Brief description of the incident"
                                        className="bg-slate-950 border-slate-800 text-slate-200"
                                        data-testid="incident-title-input"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Category *</Label>
                                        <Select value={category} onValueChange={setCategory}>
                                            <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200" data-testid="incident-category-select">
                                                <SelectValue placeholder="Select category" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {categories.map(cat => (
                                                    <SelectItem key={cat.id} value={cat.id} className="text-slate-200 focus:bg-slate-800">
                                                        {cat.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Severity *</Label>
                                        <Select value={severity} onValueChange={setSeverity}>
                                            <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200" data-testid="incident-severity-select">
                                                <SelectValue placeholder="Select severity" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {severities.map(sev => (
                                                    <SelectItem key={sev.id} value={sev.id} className="text-slate-200 focus:bg-slate-800">
                                                        {sev.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-slate-300">Description *</Label>
                                    <Textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="Detailed description of what happened..."
                                        className="bg-slate-950 border-slate-800 text-slate-200 min-h-[100px]"
                                        data-testid="incident-description-input"
                                    />
                                </div>
                                <Button
                                    onClick={handleCreateIncident}
                                    className="w-full brand-btn"
                                    data-testid="submit-incident-button"
                                >
                                    Report Incident
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Stats */}
                {openCount > 0 && (
                    <Card className="bg-rose-500/10 border-rose-500/20">
                        <CardContent className="py-4">
                            <div className="flex items-center gap-3">
                                <AlertCircle className="h-5 w-5 text-rose-400" />
                                <span className="text-rose-200">
                                    <strong>{openCount}</strong> open incident(s) requiring attention
                                </span>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Filter Tabs */}
                <Tabs value={filter} onValueChange={setFilter}>
                    <TabsList className="bg-slate-900 border border-slate-800">
                        <TabsTrigger value="all" className="brand-btn data-[state=active]:text-white">
                            All
                        </TabsTrigger>
                        <TabsTrigger value="open" className="brand-btn data-[state=active]:text-white">
                            Open
                        </TabsTrigger>
                        <TabsTrigger value="resolved" className="brand-btn data-[state=active]:text-white">
                            Resolved
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                {/* Incidents List */}
                {incidents.length > 0 ? (
                    <div className="space-y-4">
                        {incidents.map(incident => (
                            <Card
                                key={incident.id}
                                className={cn(
                                    'bg-slate-900 border-slate-800',
                                    !incident.is_resolved && 'border-l-4 border-l-rose-500'
                                )}
                                data-testid={`incident-card-${incident.id}`}
                            >
                                <CardContent className="py-4">
                                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                                        <div className="flex items-start gap-4">
                                            <div className={cn(
                                                'h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0',
                                                incident.is_resolved ? 'bg-emerald-500/10' : 'bg-rose-500/10'
                                            )}>
                                                {incident.is_resolved ? (
                                                    <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                                                ) : (
                                                    <AlertTriangle className="h-5 w-5 text-rose-400" />
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h3 className="font-semibold text-slate-100">{incident.title}</h3>
                                                    <Badge className={getSeverityBadge(incident.severity).color}>
                                                        {capitalize(incident.severity)}
                                                    </Badge>
                                                    <Badge variant="outline" className="border-slate-700 text-slate-400">
                                                        {capitalize(incident.category)}
                                                    </Badge>
                                                </div>
                                                <p className="text-sm text-slate-400 mt-2">{incident.description}</p>
                                                <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                                                    <span>Reported by {incident.reported_by_name}</span>
                                                    <span>{formatDateTime(incident.reported_at)}</span>
                                                </div>
                                                {incident.is_resolved && (
                                                    <div className="mt-3 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                                                        <p className="text-sm text-emerald-400">
                                                            <strong>Corrective Action:</strong> {incident.corrective_action}
                                                        </p>
                                                        <p className="text-xs text-emerald-400/70 mt-1">
                                                            Resolved by {incident.resolved_by} on {formatDateTime(incident.resolved_at)}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {!incident.is_resolved && isManager && (
                                            <Button
                                                variant="outline"
                                                onClick={() => {
                                                    setSelectedIncident(incident);
                                                    setResolveDialogOpen(true);
                                                }}
                                                className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                                                data-testid={`resolve-incident-${incident.id}`}
                                            >
                                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                                Resolve
                                            </Button>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="py-12">
                            <div className="empty-state">
                                <AlertTriangle className="empty-state-icon" />
                                <p className="empty-state-title">No incidents</p>
                                <p className="empty-state-description">
                                    {filter === 'all' 
                                        ? 'No incidents have been reported yet.'
                                        : filter === 'open'
                                            ? 'Great! No open incidents.'
                                            : 'No resolved incidents found.'}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Resolve Dialog */}
                <Dialog open={resolveDialogOpen} onOpenChange={(open) => {
                    setResolveDialogOpen(open);
                    if (!open) {
                        setSelectedIncident(null);
                        setCorrectiveAction('');
                    }
                }}>
                    <DialogContent className="bg-slate-900 border-slate-800">
                        <DialogHeader>
                            <DialogTitle className="text-slate-100">Resolve Incident</DialogTitle>
                        </DialogHeader>
                        {selectedIncident && (
                            <div className="space-y-4 pt-4">
                                <div className="p-4 bg-slate-800/50 rounded-lg">
                                    <h4 className="font-medium text-slate-200">{selectedIncident.title}</h4>
                                    <p className="text-sm text-slate-400 mt-1">{selectedIncident.description}</p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-slate-300">Corrective Action Taken *</Label>
                                    <Textarea
                                        value={correctiveAction}
                                        onChange={(e) => setCorrectiveAction(e.target.value)}
                                        placeholder="Describe the corrective action taken to resolve this incident..."
                                        className="bg-slate-950 border-slate-800 text-slate-200 min-h-[100px]"
                                        data-testid="resolve-corrective-action"
                                    />
                                </div>
                                <Button
                                    onClick={handleResolveIncident}
                                    className="w-full bg-emerald-600 text-white hover:bg-emerald-500"
                                    data-testid="confirm-resolve-button"
                                >
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Mark as Resolved
                                </Button>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};

export default Incidents;
